import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { loadAvailableSources } from 'js/actions/rubric-actions';
import assessmentTemplates from 'js/data/assessment-templates';
import RubricQuestionPickerModal from './RubricQuestionPickerModal';

function normalizeAnswers(answers) {
  if (!Array.isArray(answers)) {
    return [];
  }
  return answers.map((a, idx) => ({
    key: String(a.id ?? a.key ?? `answer-${idx}`),
    name: a.text ?? '',
    text: a.text ?? '',
    value: String(a.value ?? ''),
    ordinal: typeof a.ordinal === 'number' ? a.ordinal : idx,
    is_na: Boolean(a.is_na),
  }));
}

/** One question row with fields required by `addRubricQuestionFromSource`. */
function withSourceMeta(
  assessmentKey,
  assessmentName,
  questionSetKey,
  questionSetName,
  questionSetCategory,
  q
) {
  const text = q.text ?? '';
  return {
    ...q,
    assessment_key: assessmentKey,
    assessment_name: assessmentName,
    question_set_key: questionSetKey,
    question_set_name: questionSetName ?? questionSetKey,
    question_set_category: questionSetCategory ?? '',
    question_text: text,
  };
}

/** Config may be `{ slug: "Display Name" }` from API, or `{ slug: { display_name } }` from templates. */
function lookupCategoryEntry(categoryKey, categoryMap) {
  if (!categoryMap || typeof categoryMap !== 'object') {
    return undefined;
  }
  const key = String(categoryKey ?? '').trim();
  if (!key) {
    return undefined;
  }
  if (Object.prototype.hasOwnProperty.call(categoryMap, key)) {
    return categoryMap[key];
  }
  const lower = key.toLowerCase();
  for (const k of Object.keys(categoryMap)) {
    if (k.toLowerCase() === lower) {
      return categoryMap[k];
    }
  }
  return undefined;
}

function resolveCategoryDisplayName(rawCategory, categoryMap) {
  const key = String(rawCategory ?? '').trim();
  if (!key) {
    return '';
  }
  const mapped = lookupCategoryEntry(key, categoryMap);
  if (mapped == null || mapped === '') {
    return key;
  }
  if (typeof mapped === 'string') {
    const name = mapped.trim();
    return name || key;
  }
  const mappedName = String(mapped?.display_name ?? '').trim();
  return mappedName || key;
}

function mapQuestionSets(
  assessmentKey,
  assessmentName,
  questionSets,
  categoryMap = {}
) {
  return (questionSets ?? []).map((qs) => {
    const resolvedCategory = resolveCategoryDisplayName(
      qs.category,
      categoryMap
    );
    return {
      key: qs.key,
      name: qs.name ?? qs.key,
      ordinal: qs.ordinal,
      category: resolvedCategory,
      questions: (qs.questions ?? [])
        .filter((q) => q.key)
        .map((q) =>
          withSourceMeta(
            assessmentKey,
            assessmentName,
            qs.key,
            qs.name,
            resolvedCategory,
            {
              ...q,
              answers: normalizeAnswers(q.answers ?? q.choices),
            }
          )
        ),
    };
  });
}

function apiAssessmentToPickerGroup(row) {
  const name = row.name ?? row.key;
  /** Prefer assessment `config.categories` ({ ordinal, display_name }) over flattened `categories`. */
  const categoryMap = row.config?.categories ?? row.categories ?? {};
  return {
    key: row.key,
    primaryName: name,
    aliases: row.aliases ?? [],
    lastRunAt: row.last_run_at ?? null,
    runCount: row.run_count ?? 0,
    exponentialDecayScore: row.exponential_decay_score ?? 0,
    sourceRecency: row.source_recency_at_ms ?? 0,
    questionSets: mapQuestionSets(
      row.key,
      name,
      row.question_sets,
      categoryMap
    ),
  };
}

function legacyTemplateToPickerGroup(t) {
  const key = t.key;
  const name = t.name ?? key;
  const a = t.assessment ?? {};
  let questionSets = [];

  if (
    Array.isArray(a.question_sets) &&
    a.question_sets.some((qs) => (qs.questions ?? []).length > 0)
  ) {
    questionSets = mapQuestionSets(
      key,
      name,
      a.question_sets,
      a.config?.categories
    );
  } else if (a.pages && typeof a.pages === 'object') {
    questionSets = Object.values(a.pages)
      .filter((p) => p.pageType === 'questionSet')
      .sort(
        (x, y) =>
          (x.ordinal ?? 0) - (y.ordinal ?? 0) ||
          String(x.key).localeCompare(String(y.key))
      )
      .map((page) => {
        const qsKey = page.key;
        const qsName = page.name ?? page.title ?? qsKey;
        const elements = page.elements ?? {};
        const questions = Object.values(elements)
          .filter((el) => el.elementType === 'question' && el.key)
          .sort(
            (x, y) =>
              (x.ordinal ?? 0) - (y.ordinal ?? 0) ||
              String(x.key).localeCompare(String(y.key))
          )
          .map((el) =>
            withSourceMeta(key, name, qsKey, qsName, '', {
              key: el.key,
              name: el.name ?? el.text ?? el.key,
              text: el.text ?? '',
              type: el.type,
              ordinal: el.ordinal,
              last_asked_at: null,
              answers: normalizeAnswers(el.answers ?? el.choices),
            })
          );

        return {
          key: qsKey,
          name: qsName,
          ordinal: page.ordinal,
          questions,
        };
      });
  }

  return {
    key,
    primaryName: name,
    aliases: [],
    lastRunAt: null,
    runCount: 0,
    exponentialDecayScore: 0,
    sourceRecency: 0,
    questionSets,
  };
}

function buildPickerAssessmentGroups(apiAssessments, defaults) {
  const byKey = new Map();

  for (const row of apiAssessments) {
    if (!row?.key) {
      continue;
    }
    byKey.set(row.key, apiAssessmentToPickerGroup(row));
  }

  for (const t of defaults) {
    if (!t?.key || !t?.assessment || byKey.has(t.key)) {
      continue;
    }
    byKey.set(t.key, legacyTemplateToPickerGroup(t));
  }

  return [...byKey.values()]
    .map((g) => ({
      ...g,
      questionSets: (g.questionSets ?? []).filter(
        (qs) => (qs.questions ?? []).length > 0
      ),
    }))
    .filter((g) => (g.questionSets ?? []).length > 0)
    .sort((a, b) => {
      const scoreDiff =
        (b.exponentialDecayScore ?? 0) - (a.exponentialDecayScore ?? 0);
      if (scoreDiff !== 0) {
        return scoreDiff;
      }
      return a.primaryName.localeCompare(b.primaryName);
    });
}

export default function RubricQuestionPickerModalContainer({
  isOpen,
  onClose,
  onAddQuestions,
  onAddQuestionsWithSets,
  pickerMode = 'addQuestions',
}) {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();
  const queryId = `rubric-available-sources-${workspaceId ?? 'none'}`;
  const queryState = useSelector((state) => state.queries[queryId]);

  const refresh = useCallback(() => {
    if (!workspaceId) {
      return Promise.resolve();
    }
    return dispatch(loadAvailableSources(queryId));
  }, [dispatch, workspaceId, queryId]);

  useEffect(() => {
    if (isOpen) {
      refresh();
    }
  }, [isOpen, refresh]);

  const assessmentGroups = useMemo(() => {
    const apiList = Array.isArray(queryState?.result) ? queryState.result : [];
    const defaults = (assessmentTemplates ?? []).filter((t) => !!t?.assessment);
    return buildPickerAssessmentGroups(apiList, defaults);
  }, [queryState?.result]);

  const loadError = Boolean(queryState?.error);
  const loading =
    isOpen && Boolean(workspaceId) && queryState?.isLoading !== false;

  return (
    <RubricQuestionPickerModal
      isOpen={isOpen}
      onClose={onClose}
      onAddQuestions={onAddQuestions}
      onAddQuestionsWithSets={onAddQuestionsWithSets}
      pickerMode={pickerMode}
      assessmentGroups={assessmentGroups}
      loading={loading}
      loadError={loadError}
    />
  );
}
